/**
 * bootstrap.ts — Roamer Platform™ boot sequence (Platform Integrity Audit repair).
 *
 * Fixes the audit's #1 Critical finding: Roamer Core™ was fully built but
 * `roamerCore.initialize()` was never called anywhere, so the ten platform
 * modules and the v2 engines never joined the runtime — they were
 * service-layer islands only reachable from tests and the Developer Center™.
 *
 * BOOT ORDER (this is the documented contract in every module header —
 * "Call attachToCore() during app bootstrap (after roamerCore.initialize())"):
 *   1. roamerCore.initialize()   — registers the built-in engine adapters
 *      (Location / Fusion / Intelligence), bridges native engine events onto
 *      the core bus, installs default background jobs, starts the scheduler.
 *   2. registerEngine()          — v2 engines that have no built-in adapter
 *      (Learning, Community Intelligence, Predictive, Knowledge, Concierge).
 *      Health checks are REAL probe calls — never a fabricated `() => true`
 *      unless the engine is genuinely stateless-available.
 *   3. attachToCore()            — all ten platform modules, Passport →
 *      Marketplace. Each subscribes to core bus events only (module rule).
 *   4. pluginRegistry.attach()   — Developer Center™ observer (idempotent).
 *
 * FAILURE POLICY: boot is best-effort and must never crash the app. Every
 * step is isolated; failures are logged and counted, and the UI renders
 * regardless. `bootRoamerPlatform()` is idempotent — repeat calls no-op.
 */

import { roamerCore } from './RoamerCore';
import { resolveSecurityConfig, localOperatorSession } from './security/securityConfig';
import { ALL_CORE_EVENT_NAMES } from './events/CoreEventBus';
import { eventContractRegistry } from './events/EventContractRegistry';
import { registerAllEventContracts } from './events/contracts';
import type { EngineAdapter } from './registry/EngineRegistry';

// v2 engines (adapters registered here — no builtin adapter covers them)
import { roamerLearningEngine } from '@/services/learning';
import { roamerCommunityIntelligenceEngine } from '@/services/community-intelligence';
import { roamerPredictiveIntelligenceEngine } from '@/services/predictive';
import { roamerKnowledgeEngine } from '@/services/knowledge';
import { roamerAIConcierge } from '@/services/concierge';

// The ten platform modules
import { roamerPassportModule } from '@/services/passport';
import { roamerWalletModule } from '@/services/wallet';
import { roamerVisaModule } from '@/services/visa';
import { roamerStayModule } from '@/services/stay';
import { roamerPlannerModule } from '@/services/planner';
import { roamerConnectModule } from '@/services/connect';
import { roamerCareerModule } from '@/services/career';
import { roamerNetworkModule } from '@/services/network';
import { roamerHealthModule } from '@/services/health';
import { roamerMarketplaceModule } from '@/services/marketplace';

// Developer Center™ observer
import { hydrationOrchestrator } from '@/services/HydrationOrchestrator';
import { storageManager } from '@/services/storage/StorageManager';
import { pluginRegistry } from '@/services/plugins';

// WorkRoamer OS v2 Migration: legacy mission-stack bridge (observe-only)
import { buildLegacyEngineAdapters, bridgeLegacyEvents } from './legacy/legacyBridge';

// ─── v2 engine adapters ────────────────────────────────────────────────────────
// Health checks call a real cheap read on the engine and fail closed on throw.

function probe(fn: () => unknown): boolean {
  try { fn(); return true; } catch { return false; }
}

function buildV2EngineAdapters(): EngineAdapter[] {
  return [
    {
      id: 'learning',
      name: 'Roamer Learning Engine™',
      version: '2.0.0',
      capabilities: ['behavioral-signals', 'preference-inference', 'evidence-decay'],
      healthCheck: () => probe(() => roamerLearningEngine.getSignals()),
    },
    {
      id: 'community-intelligence',
      name: 'Roamer Community Intelligence Engine™',
      version: '1.0.0',
      capabilities: ['community-signals', 'reputation-weighting', 'opt-in-sharing'],
      healthCheck: () => probe(() => roamerCommunityIntelligenceEngine.getLocationIntelligence('health-probe')),
    },
    {
      id: 'predictive',
      name: 'Roamer Predictive Intelligence Engine™',
      version: '1.0.0',
      capabilities: ['predictions', 'opportunity-forecast', 'deterministic-rules'],
      healthCheck: () => probe(() => roamerPredictiveIntelligenceEngine.getPredictions()),
    },
    {
      id: 'knowledge',
      name: 'Roamer Knowledge Engine™',
      version: '1.0.0',
      capabilities: ['knowledge-records', 'versioning', 'semantic-search'],
      healthCheck: () => probe(() => roamerKnowledgeEngine.get('health-probe')),
    },
    {
      id: 'concierge',
      name: 'Roamer AI Concierge™',
      version: '1.0.0',
      capabilities: ['intent-handling', 'context-gathering', 'provenance-labels'],
      healthCheck: () => probe(() => roamerAIConcierge.getSettings()),
    },
  ];
}

// ─── Boot ──────────────────────────────────────────────────────────────────────

export interface BootReport {
  ok: boolean;                 // true when core initialized (even with partial failures below)
  alreadyBooted: boolean;
  enginesRegistered: number;   // v2 engines successfully registered (of 5)
  legacyEnginesBridged: number; // legacy engines registered under core supervision (of 9)
  modulesAttached: number;     // platform modules successfully attached (of 10)
  errors: string[];            // human-readable step failures
}

let booted = false;

/** Idempotent platform boot. Called once from main.tsx before first render. */
export function bootRoamerPlatform(now: number = Date.now()): BootReport {
  const report: BootReport = { ok: false, alreadyBooted: booted, enginesRegistered: 0, legacyEnginesBridged: 0, modulesAttached: 0, errors: [] };
  if (booted) { report.ok = roamerCore.isInitialized; return report; }

  // 1. Core (registers Location / Fusion / Intelligence builtin adapters).
  // The `booted` latch is only set AFTER a successful init so a failed boot
  // stays retryable (review advisory: don't latch a permanently broken state).
  // 0. Event Contract System™: arm the contract registry BEFORE any publish
  // so even boot-time events (EngineRegistered, …) are contract-validated.
  try {
    registerAllEventContracts();
  } catch (err) {
    report.errors.push(`registerAllEventContracts: ${err instanceof Error ? err.message : String(err)}`);
  }

  // 0.5. BETA GATE HARDENING™ — security is fail-closed by default.
  // OPERATOR IDENTITY™ (2.06.0): a real auth backend now exists at /api/auth/*.
  // Shell calls useGetMe() on every protected route to verify the session cookie;
  // engines boot here from localStorage regardless (boot is auth-agnostic).
  // Cloud hydration is fire-and-forget: it uses the cookie session automatically.
  try {
    const security = resolveSecurityConfig();
    if (security.ignoredInsecureOverride) {
      console.warn('[roamer-boot] VITE_ENFORCE_SECURITY=false IGNORED — production always enforces security.');
    }
    if (!roamerCore.security.getSession()) {
      roamerCore.security.setSession(localOperatorSession(now));
    }
  } catch (err) {
    report.errors.push(`securityBoot: ${err instanceof Error ? err.message : String(err)}`);
  }

  try {
    roamerCore.initialize(now);
    booted = true;
    report.ok = true;
  } catch (err) {
    report.errors.push(`core.initialize: ${err instanceof Error ? err.message : String(err)}`);
    console.error('[roamer-boot] Roamer Core failed to initialize — platform modules skipped.', err);
    return report; // nothing below is meaningful without the core
  }

  // 2. v2 engine adapters.
  for (const adapter of buildV2EngineAdapters()) {
    try {
      roamerCore.registerEngine(adapter, now);
      report.enginesRegistered += 1;
    } catch (err) {
      report.errors.push(`registerEngine(${adapter.id}): ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // 2b. Legacy bridge (WorkRoamer OS v2 Migration): register the legacy
  // mission stack under core supervision + mirror legacy CustomEvents onto
  // the bus. Observe-only — legacy behavior is unchanged (backward compat).
  for (const adapter of buildLegacyEngineAdapters()) {
    try {
      roamerCore.registerEngine(adapter, now);
      report.legacyEnginesBridged += 1;
    } catch (err) {
      report.errors.push(`registerEngine(${adapter.id}): ${err instanceof Error ? err.message : String(err)}`);
    }
  }
  try {
    bridgeLegacyEvents(roamerCore);
  } catch (err) {
    report.errors.push(`bridgeLegacyEvents: ${err instanceof Error ? err.message : String(err)}`);
  }

  // 3. Platform modules (order = module number; each is bus-only and isolated).
  const modules: Array<{ id: string; attach: () => void }> = [
    { id: 'passport',    attach: () => roamerPassportModule.attachToCore(roamerCore, now) },
    { id: 'wallet',      attach: () => roamerWalletModule.attachToCore(roamerCore, now) },
    { id: 'visa',        attach: () => roamerVisaModule.attachToCore(roamerCore, now) },
    { id: 'stay',        attach: () => roamerStayModule.attachToCore(roamerCore, now) },
    { id: 'planner',     attach: () => roamerPlannerModule.attachToCore(roamerCore, now) },
    { id: 'connect',     attach: () => roamerConnectModule.attachToCore(roamerCore, now) },
    { id: 'career',      attach: () => roamerCareerModule.attachToCore(roamerCore, now) },
    { id: 'network',     attach: () => roamerNetworkModule.attachToCore(roamerCore, now) },
    { id: 'health',      attach: () => roamerHealthModule.attachToCore(roamerCore, now) },
    { id: 'marketplace', attach: () => roamerMarketplaceModule.attachToCore(roamerCore, now) },
  ];
  for (const m of modules) {
    try {
      m.attach();
      report.modulesAttached += 1;
    } catch (err) {
      report.errors.push(`attachToCore(${m.id}): ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // 3b. SUPABASE FOUNDATION™ — cloud persistence wiring.
  // First give the StorageManager a Core-bus publisher (DataSynchronized /
  // PersistenceFailed / SystemStateChanged flow through the contracted bus),
  // then hydrate the first-wave domains (Profiles, Backpack™, Vault™,
  // Operator Profile, Operator Settings). localStorage answered all
  // synchronous boots above; a cloud copy is adopted only when strictly
  // newer. Fire-and-forget — boot never blocks on the network.
  try {
    storageManager.attachEventPublisher((name, payload) => {
      roamerCore.publish(name, 'storage-manager', payload, Date.now());
    });
    // OPERATOR IDENTITY™ Phase 3 (2.08.0): all cloud hydration now flows
    // through the single deterministic Hydration Orchestrator (13-step
    // pipeline, independent failure isolation, OperatorHydrated +
    // CloudHydrationCompleted bus events, restore timeline diagnostics).
    void hydrationOrchestrator.runFullHydration();
  } catch (err) {
    report.errors.push(`cloudHydration: ${err instanceof Error ? err.message : String(err)}`);
  }

  // 4. Developer Center™ plugin observer (idempotent; safe after everything).
  try {
    pluginRegistry.attach();
  } catch (err) {
    report.errors.push(`pluginRegistry.attach: ${err instanceof Error ? err.message : String(err)}`);
  }

  // 5. Startup contract verification: every known event must have a contract,
  // versions consistent, no duplicates. Findings warn — boot never fails on
  // documentation gaps (backward-compat guarantee).
  try {
    const findings = eventContractRegistry.verifyCoverage(ALL_CORE_EVENT_NAMES);
    if (findings.length > 0) console.warn('[roamer-boot] event contract findings:', findings);
  } catch (err) {
    report.errors.push(`contractVerification: ${err instanceof Error ? err.message : String(err)}`);
  }

  // Boot verification (BETA GATE HARDENING™): security mode, environment,
  // authorization state, and configuration source — always in the boot log.
  const sec = roamerCore.getSecurityInfo(now);
  const secSummary = `security=${sec.enforcedNow ? 'ENFORCED' : 'DEV-OPEN'} env=${sec.environment} auth=${sec.hasValidSession ? 'session' : 'none'} src=${sec.source}`;
  const summary = `[roamer-boot] core=on engines=+${report.enginesRegistered}/5 legacy-bridged=${report.legacyEnginesBridged}/9 modules=${report.modulesAttached}/10 contracts=${eventContractRegistry.getSnapshot().totalContracts} ${secSummary}`;
  if (report.errors.length > 0) console.warn(`${summary} errors=${report.errors.length}`, report.errors);
  else console.info(summary);

  return report;
}
